import { useState } from "react";
import { FolderArchive, Pencil, Trash2, FileDown, Lock, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { deleteRecord, formatDatum } from "../lib/storage";
import { generatePdf } from "../lib/pdf";

export default function Arhiva({ records, settings, onEdit, onDataChanged }) {
  const [query, setQuery] = useState("");
  const [pdfBusy, setPdfBusy] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const filtered = records
    .filter((r) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        formatDatum(r.datum).includes(q) ||
        r.datum.includes(q) ||
        (r.osnovni.glavniDezurni || "").toLowerCase().includes(q) ||
        (r.tip === "nastavni" ? "nastavni" : "nenastavni").includes(q)
      );
    })
    .sort((a, b) => (a.datum < b.datum ? 1 : -1));

  const handleDelete = (id) => {
    deleteRecord(id);
    setConfirmId(null);
    onDataChanged();
    toast.success("Evidencija je obrisana");
  };

  const handlePdf = async (r) => {
    setPdfBusy(r.id);
    try {
      await generatePdf(r, settings);
      toast.success("PDF izvještaj je preuzet");
    } catch {
      toast.error("Greška pri izradi PDF-a");
    }
    setPdfBusy(null);
  };

  return (
    <div className="animate-fade-in" data-testid="arhiva-view">
      <div className="mb-6 border-b border-slate-200 pb-5">
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">Prethodne evidencije</h1>
        <p className="mt-1 text-sm text-slate-500">Sve evidencije spremljene u ovom pregledniku. Preporučujemo redoviti izvoz sigurnosne kopije.</p>
      </div>

      <div className="mb-5 relative max-w-md">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="arhiva-pretraga-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pretraži po datumu ili dežurnom učitelju..."
          className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 py-16 text-center" data-testid="arhiva-empty-state">
          <FolderArchive className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm font-semibold text-slate-600">{records.length === 0 ? "Još nema spremljenih evidencija" : "Nema rezultata pretrage"}</p>
          <p className="mt-1 max-w-sm text-xs text-slate-400">
            {records.length === 0
              ? "Evidencije se automatski spremaju u vaš preglednik dok ih ispunjavate. Možete i uvesti sigurnosnu kopiju putem gumba Uvoz u izborniku."
              : "Pokušajte s drugim pojmom pretrage."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`arhiva-red-${r.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition-colors hover:border-sky-200 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white ${r.tip === "nastavni" ? "bg-sky-600" : "bg-blue-600"}`}>
                  <span className="font-heading text-xs font-bold">{r.datum.slice(8, 10)}.{r.datum.slice(5, 7)}.</span>
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-slate-900">{formatDatum(r.datum)}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.tip === "nastavni" ? "bg-sky-50 text-sky-700 border border-sky-100" : "bg-blue-50 text-blue-700 border border-blue-100"}`}>
                      {r.tip === "nastavni" ? "Nastavni dan" : "Nenastavni dan"}
                    </span>
                    {r.primjer && (
                      <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Ogledni primjer</span>
                    )}
                    {r.pregled.zakljucano && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        <Lock className="h-3 w-3" /> Zaključano
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {r.osnovni.glavniDezurni ? `Glavni dežurni: ${r.osnovni.glavniDezurni}` : "Glavni dežurni nije upisan"}
                    {" · "}{r.posjetitelji.length} posjetitelja{" · "}{r.dogadjaji.length} sigurnosnih događaja
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  data-testid={`arhiva-uredi-${r.id}`}
                  onClick={() => onEdit(r)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-sky-700"
                >
                  <Pencil className="h-3.5 w-3.5" /> Uredi
                </button>
                <button
                  data-testid={`arhiva-pdf-${r.id}`}
                  onClick={() => handlePdf(r)}
                  disabled={pdfBusy === r.id}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
                >
                  {pdfBusy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
                </button>
                {confirmId === r.id ? (
                  <button
                    data-testid={`arhiva-potvrdi-brisanje-${r.id}`}
                    onClick={() => handleDelete(r.id)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
                  >
                    Potvrdi brisanje
                  </button>
                ) : (
                  <button
                    data-testid={`arhiva-obrisi-${r.id}`}
                    onClick={() => setConfirmId(r.id)}
                    className="rounded-xl border border-red-100 bg-red-50 p-2 text-red-500 transition-colors hover:bg-red-100"
                    title="Obriši evidenciju"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
